import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantFormat } from '../../hooks/useTenantFormat';
import { useToast } from '../../hooks/useToast';
import { api } from '../../lib/api';
import { Receipt, Search, ChevronLeft, ChevronRight, Eye, CreditCard, AlertCircle, X } from 'lucide-react';

const PAGE_SIZE = 25;
const PAYMENT_MODES = ['cash', 'upi', 'cheque', 'neft', 'imps', 'other'];
const INPUT = 'w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-900 text-sm placeholder:text-gray-400 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition-colors';

function StatusChip({ status }) {
  const map = {
    paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    partial: 'bg-amber-50 text-amber-700 border-amber-200',
    unpaid: 'bg-red-50 text-red-600 border-red-200',
  };
  return <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase border ${map[status] || map.unpaid}`} data-testid="fee-status-chip">{status || 'unpaid'}</span>;
}

function PeriodChip({ period }) {
  if (!period) return <span className="text-gray-400 text-sm">—</span>;
  return <span className="px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 text-xs font-semibold border border-indigo-100">{period}</span>;
}

export default function Fees() {
  const { token } = useAuth();
  const { formatMoney } = useTenantFormat();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('payment_status') || '');
  const [periodFilter, setPeriodFilter] = useState(searchParams.get('period') || '');

  const [collectModal, setCollectModal] = useState(null); // sale object or null

  // Generate last 6 month period options
  const periodOptions = useMemo(() => {
    const opts = [];
    const now = new Date();
    for (let i = 0; i < 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      opts.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    }
    return opts;
  }, []);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = { sale_type: 'fee_invoice', limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE };
      if (search) params.q = search;
      if (statusFilter) params.payment_status = statusFilter;
      if (periodFilter) params.from_date = `${periodFilter}-01`;
      const r = await api.salesList(token, params);
      let fetched = r.items || [];
      // Client-side period filter if backend doesn't natively filter by billing_period
      if (periodFilter) {
        fetched = fetched.filter(s => (s.fee_meta?.billing_period || '').startsWith(periodFilter));
      }
      setItems(fetched);
      setTotal(r.total ?? fetched.length);
    } catch (e) {
      if (e?.status === 403) return;
      setItems([]);
      toast.show(e?.message || 'Failed to load fees', 'error');
    } finally { setLoading(false); }
  }, [token, page, search, statusFilter, periodFilter, toast]);

  useEffect(() => { load(); }, [load]);

  // Refresh a single row after payment
  const refreshRow = async (saleId) => {
    try {
      const updated = await api.saleGet(token, saleId);
      setItems(prev => prev.map(s => s.id === saleId ? updated : s));
    } catch { load(); }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="fees-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900" data-testid="fees-title">Fees</h1>
          <p className="text-gray-500 text-sm mt-1">{total} fee invoice{total !== 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input data-testid="fees-search" placeholder="Search student..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <select data-testid="fees-status-filter" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-700 bg-white outline-none focus:border-indigo-400 cursor-pointer">
          <option value="">All Status</option>
          <option value="unpaid">Unpaid</option>
          <option value="partial">Partial</option>
          <option value="paid">Paid</option>
        </select>
        <select data-testid="fees-period-filter" value={periodFilter} onChange={e => { setPeriodFilter(e.target.value); setPage(1); }}
          className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-700 bg-white outline-none focus:border-indigo-400 cursor-pointer">
          <option value="">All Periods</option>
          {periodOptions.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="fees-table">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/80">
                <th className="text-left px-4 py-3 text-xs text-gray-500 font-bold uppercase tracking-wider">Student</th>
                <th className="text-left px-4 py-3 text-xs text-gray-500 font-bold uppercase tracking-wider">Invoice #</th>
                <th className="text-left px-4 py-3 text-xs text-gray-500 font-bold uppercase tracking-wider">Period</th>
                <th className="text-right px-4 py-3 text-xs text-gray-500 font-bold uppercase tracking-wider">Amount</th>
                <th className="text-right px-4 py-3 text-xs text-gray-500 font-bold uppercase tracking-wider">Paid</th>
                <th className="text-right px-4 py-3 text-xs text-gray-500 font-bold uppercase tracking-wider">Balance</th>
                <th className="text-left px-4 py-3 text-xs text-gray-500 font-bold uppercase tracking-wider">Status</th>
                <th className="text-left px-4 py-3 text-xs text-gray-500 font-bold uppercase tracking-wider">Date</th>
                <th className="text-right px-4 py-3 text-xs text-gray-500 font-bold uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 ? (
                [1,2,3,4,5].map(i => (
                  <tr key={i}><td colSpan={9} className="px-4 py-4"><div className="h-5 bg-gray-100 rounded animate-pulse" /></td></tr>
                ))
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-16 text-center">
                    <Receipt size={40} className="text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 text-sm" data-testid="fees-empty">No fee invoices yet. Create a Fee Plan under School & Club → Fee Plans and run billing.</p>
                  </td>
                </tr>
              ) : items.map(s => {
                const balance = (s.total || 0) - (s.amount_paid || 0);
                return (
                  <tr key={s.id} className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors" data-testid={`fee-row-${s.id}`}>
                    <td className="px-4 py-3 font-medium text-gray-900">{s.customer_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-600 font-mono text-xs">{s.invoice_number || s.id?.slice(0, 8)}</td>
                    <td className="px-4 py-3"><PeriodChip period={s.fee_meta?.billing_period} /></td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900 tabular-nums">{formatMoney(s.total)}</td>
                    <td className="px-4 py-3 text-right text-gray-600 tabular-nums">{formatMoney(s.amount_paid || 0)}</td>
                    <td className="px-4 py-3 text-right font-semibold tabular-nums" data-testid={`fee-balance-${s.id}`}>
                      <span className={balance > 0 ? 'text-red-500' : 'text-emerald-600'}>{formatMoney(balance)}</span>
                    </td>
                    <td className="px-4 py-3"><StatusChip status={s.payment_status} /></td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {s.due_date || s.sale_date ? new Date(s.due_date || s.sale_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button data-testid={`fee-view-${s.id}`} title="View" onClick={() => navigate(`/sales/${s.id}`)}
                          className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"><Eye size={16} /></button>
                        {s.payment_status !== 'paid' && (
                          <button data-testid={`fee-collect-${s.id}`} title="Collect Payment" onClick={() => setCollectModal(s)}
                            className="p-2 rounded-lg hover:bg-emerald-50 text-gray-400 hover:text-emerald-600 transition-colors"><CreditCard size={16} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-500">Page {page} of {totalPages}</p>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                <ChevronLeft size={14} /> Prev
              </button>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                Next <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Collect Payment Modal */}
      {collectModal && (
        <CollectPaymentModal
          sale={collectModal}
          token={token}
          formatMoney={formatMoney}
          onClose={() => setCollectModal(null)}
          onSuccess={(saleId) => { setCollectModal(null); refreshRow(saleId); }}
        />
      )}
    </div>
  );
}

function CollectPaymentModal({ sale, token, formatMoney, onClose, onSuccess }) {
  const toast = useToast();
  const balance = (sale.total || 0) - (sale.amount_paid || 0);
  const [amount, setAmount] = useState(String(balance > 0 ? balance : 0));
  const [mode, setMode] = useState('cash');
  const [toAccount, setToAccount] = useState('cash');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.show('Enter a valid amount', 'error'); return; }
    setSaving(true);
    try {
      await api.collectFeePayment(token, sale.id, { amount: amt, payment_mode: mode, to_account: toAccount, date: date || undefined });
      const newBalance = Math.max(0, balance - amt);
      toast.show(`Payment recorded: ${formatMoney(amt)} · Balance ${formatMoney(newBalance)}`, 'success');
      onSuccess(sale.id);
    } catch (e) {
      toast.show(e?.message || 'Payment failed', 'error');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="collect-payment-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-extrabold text-gray-900">Collect Payment</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><X size={18} /></button>
        </div>

        <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 mb-5">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Student</span>
            <span className="text-gray-900 font-semibold">{sale.customer_name || '—'}</span>
          </div>
          <div className="flex justify-between text-sm mt-1.5">
            <span className="text-gray-500">Invoice</span>
            <span className="text-gray-600 font-mono text-xs">{sale.invoice_number}</span>
          </div>
          <div className="flex justify-between text-sm mt-1.5">
            <span className="text-gray-500">Total</span>
            <span className="text-gray-900 font-semibold">{formatMoney(sale.total)}</span>
          </div>
          <div className="flex justify-between text-sm mt-1.5">
            <span className="text-gray-500">Balance Due</span>
            <span className="text-red-500 font-bold" data-testid="collect-balance">{formatMoney(balance)}</span>
          </div>
        </div>

        <div className="space-y-4">
          <label className="block" htmlFor="coll-amount">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Amount *</span>
            <input id="coll-amount" data-testid="collect-amount" type="number" className={`mt-1.5 ${INPUT}`}
              value={amount} onChange={e => setAmount(e.target.value)} placeholder="0" />
          </label>

          <label className="block" htmlFor="coll-mode">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Payment Mode</span>
            <select id="coll-mode" data-testid="collect-mode" className={`mt-1.5 ${INPUT} appearance-none cursor-pointer`}
              value={mode} onChange={e => setMode(e.target.value)}>
              {PAYMENT_MODES.map(m => <option key={m} value={m} className="bg-white">{m.toUpperCase()}</option>)}
            </select>
          </label>

          <label className="block" htmlFor="coll-account">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">To Account</span>
            <select id="coll-account" data-testid="collect-account" className={`mt-1.5 ${INPUT} appearance-none cursor-pointer`}
              value={toAccount} onChange={e => setToAccount(e.target.value)}>
              <option value="cash" className="bg-white">Cash</option>
              <option value="bank" className="bg-white">Bank</option>
            </select>
          </label>

          <label className="block" htmlFor="coll-date">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</span>
            <input id="coll-date" data-testid="collect-date" type="date" className={`mt-1.5 ${INPUT}`}
              value={date} onChange={e => setDate(e.target.value)} />
          </label>
        </div>

        <button data-testid="collect-submit" onClick={submit} disabled={saving}
          className="w-full mt-5 h-12 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold text-sm hover:shadow-lg hover:shadow-emerald-500/25 transition-all disabled:opacity-50">
          {saving ? <div className="w-5 h-5 border-2 border-white/50 border-t-white rounded-full animate-spin mx-auto" /> : 'Record Payment'}
        </button>
      </div>
    </div>
  );
}


